import React, { useState, useRef, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { 
  PenTool, 
  Type, 
  Calendar, 
  FileText, 
  ChevronLeft, 
  ChevronRight, 
  ZoomIn, 
  ZoomOut,
  Trash2,
  Move,
  MousePointer
} from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const FIELD_TYPES = [
  { type: 'signature', label: 'Signature', icon: PenTool, color: '#f59e0b', bgColor: '#fef3c7' },
  { type: 'initial', label: 'Initial', icon: Type, color: '#10b981', bgColor: '#d1fae5' },
  { type: 'date', label: 'Date', icon: Calendar, color: '#3b82f6', bgColor: '#dbeafe' },
  { type: 'text', label: 'Text', icon: FileText, color: '#8b5cf6', bgColor: '#ede9fe' }
];

const PDFAnnotator = ({ pdfUrl, fields, onFieldsChange, readOnly = false }) => {
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [selectedTool, setSelectedTool] = useState(null);
  const [selectedField, setSelectedField] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);
  const pageRef = useRef(null);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const getFieldStyle = (fieldType) => {
    const config = FIELD_TYPES.find(f => f.type === fieldType);
    return config || FIELD_TYPES[0];
  };

  const handlePageClick = (e) => {
    if (readOnly || !selectedTool || isDragging) return;
    
    const pageElement = pageRef.current?.querySelector('.react-pdf__Page__canvas');
    if (!pageElement) return;

    const rect = pageElement.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    // Default sizes based on field type
    const sizes = {
      signature: { width: 20, height: 6 },
      initial: { width: 10, height: 6 },
      date: { width: 15, height: 4 },
      text: { width: 25, height: 4 }
    };

    const size = sizes[selectedTool] || { width: 15, height: 5 };

    const newField = {
      id: crypto.randomUUID(),
      field_type: selectedTool,
      page: currentPage,
      x: Math.max(0, Math.min(100 - size.width, x - size.width / 2)),
      y: Math.max(0, Math.min(100 - size.height, y - size.height / 2)),
      width: size.width,
      height: size.height,
      label: getFieldStyle(selectedTool).label,
      required: true,
      filled: false
    };

    onFieldsChange([...fields, newField]);
    toast.success(`${getFieldStyle(selectedTool).label} field added`);
  };

  const handleFieldMouseDown = (e, field) => {
    if (readOnly) return;
    e.stopPropagation();
    
    setSelectedField(field.id);
    
    if (selectedTool === 'move' || !selectedTool) {
      setIsDragging(true);
      const pageElement = pageRef.current?.querySelector('.react-pdf__Page__canvas');
      if (pageElement) {
        const rect = pageElement.getBoundingClientRect();
        setDragOffset({
          x: e.clientX - (rect.left + (field.x / 100) * rect.width),
          y: e.clientY - (rect.top + (field.y / 100) * rect.height)
        });
      }
    }
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !selectedField || readOnly) return;
    
    const pageElement = pageRef.current?.querySelector('.react-pdf__Page__canvas');
    if (!pageElement) return;

    const rect = pageElement.getBoundingClientRect();
    const field = fields.find(f => f.id === selectedField);
    if (!field) return;

    const newX = ((e.clientX - dragOffset.x - rect.left) / rect.width) * 100;
    const newY = ((e.clientY - dragOffset.y - rect.top) / rect.height) * 100;

    const updatedFields = fields.map(f => {
      if (f.id === selectedField) {
        return {
          ...f,
          x: Math.max(0, Math.min(100 - f.width, newX)),
          y: Math.max(0, Math.min(100 - f.height, newY))
        };
      }
      return f;
    });

    onFieldsChange(updatedFields);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleDeleteField = (fieldId) => {
    onFieldsChange(fields.filter(f => f.id !== fieldId));
    setSelectedField(null);
    toast.success('Field removed');
  };

  const currentPageFields = fields.filter(f => f.page === currentPage);

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      {!readOnly && (
        <div className="flex items-center gap-2 p-3 bg-gray-100 border-b rounded-t-lg">
          <div className="flex items-center gap-1 mr-4">
            <Button
              size="sm"
              variant={selectedTool === null ? "default" : "outline"}
              onClick={() => setSelectedTool(null)}
              title="Select/Move"
            >
              <MousePointer className="w-4 h-4" />
            </Button>
          </div>
          
          <div className="h-6 w-px bg-gray-300" />
          
          <div className="flex items-center gap-1">
            {FIELD_TYPES.map(({ type, label, icon: Icon, color }) => (
              <Button
                key={type}
                size="sm"
                variant={selectedTool === type ? "default" : "outline"}
                onClick={() => setSelectedTool(type)}
                title={`Add ${label} field`}
                style={selectedTool === type ? { backgroundColor: color } : {}}
              >
                <Icon className="w-4 h-4 mr-1" />
                {label}
              </Button>
            ))}
          </div>
          
          <div className="h-6 w-px bg-gray-300 ml-2" />
          
          <div className="flex items-center gap-1 ml-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => selectedField && handleDeleteField(selectedField)}
              disabled={!selectedField}
              title="Delete selected field"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
          
          <div className="ml-auto text-sm text-gray-500">
            Click on PDF to place fields
          </div>
        </div>
      )}

      {/* PDF Viewer with annotations */}
      <div 
        ref={containerRef}
        className="flex-1 overflow-auto bg-gray-200 p-4"
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <div 
          ref={pageRef}
          className="relative inline-block cursor-crosshair"
          onClick={handlePageClick}
          style={{ cursor: selectedTool ? 'crosshair' : 'default' }}
        >
          <Document
            file={pdfUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            loading={
              <div className="flex items-center justify-center h-96 w-full bg-white rounded">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
              </div>
            }
            error={
              <div className="flex items-center justify-center h-96 w-full bg-white rounded">
                <p className="text-red-500">Failed to load PDF</p>
              </div>
            }
          >
            <Page 
              pageNumber={currentPage} 
              scale={scale}
              renderTextLayer={false}
              renderAnnotationLayer={false}
            />
          </Document>

          {/* Render field annotations */}
          {currentPageFields.map((field) => {
            const style = getFieldStyle(field.field_type);
            const Icon = style.icon;
            const isSelected = selectedField === field.id;
            
            return (
              <div
                key={field.id}
                className={`absolute border-2 rounded flex items-center justify-center cursor-move transition-all ${
                  isSelected ? 'ring-2 ring-offset-2 ring-blue-500' : ''
                }`}
                style={{
                  left: `${field.x}%`,
                  top: `${field.y}%`,
                  width: `${field.width}%`,
                  height: `${field.height}%`,
                  borderColor: style.color,
                  backgroundColor: `${style.bgColor}cc`,
                  zIndex: isSelected ? 20 : 10
                }}
                onMouseDown={(e) => handleFieldMouseDown(e, field)}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedField(field.id);
                }}
              >
                <div className="flex items-center gap-1 px-2 py-1 text-xs font-medium" style={{ color: style.color }}>
                  <Icon className="w-3 h-3" />
                  <span>{field.label || style.label}</span>
                </div>
                
                {/* Resize handles (optional for future) */}
                {isSelected && !readOnly && (
                  <div 
                    className="absolute -right-1 -bottom-1 w-3 h-3 bg-blue-500 rounded-full cursor-se-resize"
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      // TODO: Implement resize
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Page controls */}
      <div className="flex items-center justify-between p-3 bg-gray-100 border-t rounded-b-lg">
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setScale(s => Math.max(0.5, s - 0.1))}
          >
            <ZoomOut className="w-4 h-4" />
          </Button>
          <span className="text-sm text-gray-600 min-w-[60px] text-center">
            {Math.round(scale * 100)}%
          </span>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setScale(s => Math.min(2, s + 0.1))}
          >
            <ZoomIn className="w-4 h-4" />
          </Button>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage <= 1}
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-sm text-gray-600">
            Page {currentPage} of {numPages || '?'}
          </span>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setCurrentPage(p => Math.min(numPages, p + 1))}
            disabled={currentPage >= numPages}
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
        
        <div className="text-sm text-gray-500">
          {fields.length} field{fields.length !== 1 ? 's' : ''} total
        </div>
      </div>
    </div>
  );
};

export default PDFAnnotator;
